import { useEffect, useRef } from 'react';
import { Check, Info } from 'lucide-react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import type { AgentRun } from '@timato/shared';
import { FINISHED_VISIBLE_MIN, useProjectMap, useTaskNames, useTimerRuns } from '@/data/hooks';
import { Tooltip } from '@/components/ui';
import { agentSeconds, isInFlight, runSeconds } from '@/data/runs';
import { cn } from '@/lib/cn';
import { timeHM } from '@/lib/dates';
import { formatDuration } from '@/lib/time';
import { notifyInBackground } from '@/lib/notify';
import { AgentGlyph } from './AgentGlyph';

/** An hours-long run says when it started, because "3h 12m" alone stops being a useful number (9a). */
const SINCE_AFTER_SEC = 2 * 3600;

const line = 'text-[11.5px] font-semibold text-ink-2 mt-[3px] pl-[14px]';

function RunRow({ run, now, taskName, projectName, archived, onOpen }: {
  run: AgentRun; now: Date; taskName: string; projectName: string | null; archived: boolean; onOpen: () => void;
}) {
  const seconds = runSeconds(run, now);
  const elapsed = formatDuration(seconds, { zero: '0m' });

  if (!isInFlight(run)) {
    const ago = formatDuration(Math.round((now.getTime() - Date.parse(run.endedAt!)) / 1000), { zero: 'just now' });
    return (
      <div className="py-[9px]">
        <div className="flex items-center gap-2">
          <Check size={12} strokeWidth={2.6} className="shrink-0 -ml-[3px] text-ink" />
          <span className="flex-1 text-[13px] font-bold truncate">{taskName}</span>
          <button type="button" onClick={onOpen} className="shrink-0 text-[12px] font-extrabold text-ink underline underline-offset-[3px] bg-transparent border-0 p-0 cursor-pointer">Open</button>
        </div>
        <p className={`${line} m-0`}>{run.agent} finished {ago} ago · <span className="font-mono">{elapsed}</span> · go and look</p>
      </div>
    );
  }

  return (
    <div className="py-[9px] border-b border-row-divider">
      <div className="flex items-center gap-2">
        <span aria-hidden="true" className="w-[6px] h-[6px] rounded-full bg-ink-2 shrink-0 animate-run-pulse" />
        <span className={cn('flex-1 text-[13px] font-bold truncate', archived && 'text-ink-2 line-through')}>{taskName}</span>
        <span className="font-mono text-[12px] text-ink-body shrink-0">{elapsed}</span>
      </div>
      <p className={`${line} m-0 flex items-center gap-[6px]`}>
        <span>
          With {run.agent}
          {seconds >= SINCE_AFTER_SEC && <> since <span className="font-mono">{timeHM(run.startedAt)}</span></>}
          {projectName && <> · <span className="text-ink-3">{projectName}</span></>}
        </span>
        {archived && <span className="text-[10px] font-extrabold text-ink-2 border border-chip-border rounded-[4px] px-[5px] py-px tracking-[.04em]">TASK ARCHIVED</span>}
      </p>
    </div>
  );
}

/**
 * Agent runs under the goal (9a). Never a timer: no ring, no countdown, and nothing here is added to the
 * person's hours. In-flight runs first, then anything that finished in the last quarter of an hour.
 */
export function InFlightPanel({ now }: { now: Date }) {
  const runs = useTimerRuns(now);
  const projects = useProjectMap();
  const navigate = useNavigate();
  const tasks = useTaskNames((runs ?? []).map((r) => r.taskId).filter((id): id is string => !!id));

  // A run finishing is worth noticing, not interrupting: a line in the panel, a toast, a notification if hidden.
  const announced = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const r of runs ?? []) {
      if (isInFlight(r) || announced.current.has(r.id)) continue;
      announced.current.add(r.id);
      const name = tasks.get(r.taskId ?? '') ?? 'a task';
      const body = `${formatDuration(runSeconds(r, now), { zero: '0m' })} on ${name}`;
      toast(`${r.agent} finished`, { description: body, action: r.taskId ? { label: 'Open', onClick: () => void navigate(`/projects/${r.projectId ?? ''}`) } : undefined });
      notifyInBackground(`${r.agent} finished`, body);
    }
    // `now` ticks every second; only the run list should re-announce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runs]);

  if (!runs?.length) return null;
  const inFlight = runs.filter(isInFlight);

  return (
    <>
      <div className="h-px bg-track mt-3 mb-1" />
      <div className="flex items-center justify-between">
        {/* What the section is, a hover or a tap away (TW-48), as Parked timers does it: read once, then it stays quiet. */}
        <Tooltip tap wide side="right" label={`What your coding agents, such as Claude Code, are working on now, as they report it through Timato's MCP server. A finished run stays here for ${FINISHED_VISIBLE_MIN} minutes. Their time is never added to yours. Connect an agent in Settings › Agents.`}>
          <button type="button" className="inline-flex items-center gap-[7px] p-0 border-0 bg-transparent text-eyebrow text-ink-2 cursor-help hover:text-ink">
            <AgentGlyph />In flight<Info size={12} aria-hidden="true" className="text-ink-3" />
          </button>
        </Tooltip>
        <span className="font-mono text-[11px] font-semibold text-ink-3">{inFlight.length}</span>
      </div>
      <div className="flex flex-col">
        {runs.map((r) => (
          <RunRow key={r.id} run={r} now={now}
            taskName={tasks.get(r.taskId ?? '') ?? r.note ?? 'Untitled'}
            projectName={r.projectId ? projects.get(r.projectId)?.name ?? null : null}
            archived={!!r.projectId && !!projects.get(r.projectId)?.archivedAt}
            onOpen={() => void navigate(r.projectId ? `/projects/${r.projectId}` : '/projects')} />
        ))}
      </div>
      <p className="sr-only">Agent time is not counted in your hours.</p>
      <p className="text-[11px] font-semibold text-ink-3 m-0">{formatDuration(agentSeconds(inFlight, now), { zero: '0m' })} of agent time, not counted in yours</p>
    </>
  );
}
